"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { ShieldCheck } from "lucide-react";
const faqData = [{
  id: "item-1",
  question: "What makes IronPeak Men’s Health different?",
  answer: "IronPeak is physician-led and focused on clear, medically responsible testosterone care for Texas men. We start with symptoms, history, labs, and safety markers before deciding whether TRT or any other therapy is appropriate."
}, {
  id: "item-2",
  question: "Who is Basecamp TRT for?",
  answer: "Basecamp is designed for men who want a focused starting point for evaluating and treating low testosterone when clinically appropriate. It is best for men who want physician-led TRT care, ongoing support, and a clear plan without starting with a larger package."
}, {
  id: "item-3",
  question: "Does everyone who books a consultation get testosterone?",
  answer: "No. Testosterone is prescribed only when medically appropriate after a physician review of your symptoms, medical history, lab results, and risk factors. If TRT is not appropriate, we will discuss other possible next steps."
}, {
  id: "item-4",
  question: "What is the difference between Basecamp, Ascent, and Summit?",
  answer: <>
        <ul className="list-disc pl-5 space-y-2 mb-4">
          <li>
            <strong>Basecamp</strong> is the recommended starting point for
            focused, physician-led TRT care.
          </li>
          <li>
            <strong>Ascent</strong> includes labs and adds support for recovery,
            metabolism, wellness, and clinically indicated NAD+ or vitamin
            injections.
          </li>
          <li>
            <strong>Summit</strong> is the highest-touch option for men who want
            included labs, advanced cardiometabolic review, and more
            comprehensive physician-led support.
          </li>
        </ul>
        <p>
          Most men start with Basecamp and upgrade only if their goals, labs, or
          medical picture call for more support.
        </p>
      </>
}, {
  id: "item-5",
  question: "Are labs included?",
  answer: "Labs are included in Ascent and Summit. Basecamp includes physician review and interpretation of relevant labs, but lab testing itself is not included in the Basecamp monthly price unless otherwise specified."
}, {
  id: "item-6",
  question: "Are NAD+ and vitamin injections included?",
  answer: "NAD+ therapy and vitamin injections are included in Ascent and Summit when clinically indicated. They are not automatically prescribed for every patient; they are used when they fit your goals, labs, and clinical picture."
}, {
  id: "item-7",
  question: "Are GLP-1 medications included?",
  answer: "No. GLP-1 medications are billed separately. They are only available to Ascent or Summit patients and only when medically appropriate after physician review."
}, {
  id: "item-8",
  question: "Are supplements included?",
  answer: "Supplement recommendations are included in Ascent and Summit, but supplements are purchased separately. This allows recommendations to be personalized without forcing every patient into the same supplement bundle."
}, {
  id: "item-9",
  question: "Do you accept insurance?",
  answer: "No. IronPeak Men’s Health is a direct-pay medical practice and does not accept insurance. This allows for transparent pricing, longer visits, and care that is not restricted by insurance requirements."
}, {
  id: "item-10",
  question: "How long does it take to feel different on TRT?",
  answer: "Response times vary. Some men notice changes earlier, while others require more time, dose adjustments, or evaluation of other health factors. TRT is not a quick fix, and progress depends on labs, symptoms, sleep, nutrition, training, medical history, and consistency."
}, {
  id: "item-11",
  question: "Can I upgrade or change packages later?",
  answer: "Yes. Many patients start with Basecamp and later move to Ascent or Summit if they need included labs, additional recovery support, metabolic support, or a higher-touch care plan. Your package can be adjusted based on clinical need and preference."
}, {
  id: "item-12",
  question: "Are injections safe to do at home?",
  answer: "When injectable therapy is prescribed, patients receive education on proper administration, dosing, storage, and safety. Many therapies are commonly administered at home, but the specific plan depends on what is prescribed and clinically appropriate."
}, {
  id: "item-13",
  question: "Is telemedicine TRT legal in Texas?",
  answer: "Yes. IronPeak provides physician-led telemedicine care for Texas patients in accordance with applicable medical and prescribing rules. Medications are prescribed only when medically indicated and sourced through licensed pharmacies."
}, {
  id: "item-14",
  question: "How do I get started?",
  answer: "Book a free consultation. We will review your symptoms, goals, medical history, and appropriate labs, then help determine whether Basecamp TRT or another level of care makes sense for you."
}];
export default function FrequentlyAskedQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  useEffect(() => {
    const calendlyCssHref = "https://assets.calendly.com/assets/external/widget.css";
    const calendlyScriptSrc = "https://assets.calendly.com/assets/external/widget.js";
    if (!document.querySelector(`link[href="${calendlyCssHref}"]`)) {
      const link = document.createElement("link");
      link.href = calendlyCssHref;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (!document.querySelector(`script[src="${calendlyScriptSrc}"]`)) {
      const script = document.createElement("script");
      script.src = calendlyScriptSrc;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  const handleScheduleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (typeof window !== "undefined" && (window as any).Calendly) {
      (window as any).Calendly.initPopupWidget({
        url: "https://calendly.com/mfloresdo/30min"
      });
    }
  };
  return <>
      <section id="pricing-guarantee" className="py-10 md:py-14 bg-background overflow-hidden">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl border border-muted-light bg-card shadow-soft overflow-hidden">
              <div className="text-center px-5 pt-6 pb-5 md:px-8 md:pt-8 md:pb-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary-light-bg px-4 py-2 mb-4">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-primary">
                    30-Day Satisfaction Guarantee
                  </span>
                </div>

                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3 leading-tight">
                  Risk-Free Care. Real Confidence.
                </h2>

                <p className="text-base md:text-lg text-muted-dark leading-relaxed max-w-3xl mx-auto">
                  We stand behind the quality of our care. If you are not 100%
                  satisfied with your IronPeak experience within your first 30
                  days, we will make it right or refund your money.
                </p>
              </div>

              <div className="px-3 pb-3 md:px-5 md:pb-5">
                <img src="https://ironpeakmenshealth.com/wp-content/uploads/2026/05/30-day-guarantee.png" alt="IronPeak Men’s Health 30-day satisfaction guarantee" className="w-full max-w-4xl mx-auto rounded-xl shadow-medium" />
              </div>

              <div className="border-t border-muted-light px-5 py-4 md:px-8 text-center">
                <p className="text-xs md:text-sm text-muted-dark leading-relaxed max-w-3xl mx-auto">
                  Treatment decisions remain based on medical appropriateness,
                  physician review, and patient safety. Medications,
                  supplements, outside services, and third-party costs may be
                  excluded.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background py-12 sm:py-16 lg:py-20 overflow-hidden">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="text-center mb-10 sm:mb-12">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl text-foreground mb-4">
                Frequently Asked Questions
              </h2>

              <p className="text-base sm:text-lg text-muted-dark leading-relaxed max-w-2xl mx-auto">
                Clear answers about Basecamp TRT, package options, included
                services, and how physician-led care works at IronPeak Men’s
                Health.
              </p>
            </div>

            <div ref={ref} className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
              <Accordion type="single" collapsible className="w-full space-y-4">
                {faqData.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="rounded-xl border border-border bg-card px-4 sm:px-6 shadow-soft transition-colors duration-[var(--duration-medium)] hover:border-secondary/50 overflow-hidden">
                    <AccordionTrigger className="text-left text-base font-semibold hover:text-secondary hover:no-underline sm:text-lg leading-snug">
                      {item.question}
                    </AccordionTrigger>

                    <AccordionContent className="text-muted-dark leading-relaxed text-sm sm:text-base">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <div className="mt-10 sm:mt-12 flex flex-col items-center justify-center text-center">
              <Button onClick={handleScheduleClick} size="lg" className="w-full max-w-[340px] sm:w-auto sm:max-w-none inline-flex items-center justify-center gap-2 whitespace-normal text-center leading-snug text-sm sm:text-base font-semibold rounded-sm bg-accent text-accent-foreground hover:bg-accent-hover hover:scale-105 active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 shadow-soft hover:shadow-medium transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)] px-6 sm:px-8 py-3">
                Book Your Free Consultation
              </Button>

              <p className="text-xs sm:text-sm text-muted-dark leading-relaxed mt-4 max-w-2xl">
                Treatment is prescribed only when medically appropriate after
                physician review.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>;
}